import React, { FC } from 'react'

import { Box, Typography, useTheme } from '@mui/material'
import TableCell from '@mui/material/TableCell'
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns'
import { de, enUS, es, fr, it, uk } from 'date-fns/locale'
import { isNull } from 'lodash'
import { useTranslation } from 'react-i18next'

import { TradeStatus } from '../../pages/TradingLog/types'
import { useAppSelector } from '../../store/hooks'
import { currencyFormatter } from '../../utils'

import { JournalingCell } from './JournalingCell'
import { ProfitCell } from './ProfitCell'
import { TradeStatusCell } from './TradeStatusCell'
import { AvailableTableTypes, CommonTableColumnsEnum, TableBodyCellProps } from './types'

export const TableBodyCell: FC<TableBodyCellProps<AvailableTableTypes>> = ({
  id,
  type,
  value,
  selectedRow,
  currency,
  error,
  onClick,
}) => {
  const { spacing, palette } = useTheme()
  const { i18n, t } = useTranslation()
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  const dateLocale = { en: enUS, uk, es, fr, it, de }[language] || enUS
  let cellContent: React.ReactNode = !isNaN(value) ? Number(Number(value).toFixed(2)) : value

  const currentAccount = useAppSelector(({ userAccounts }) => userAccounts.currentAccount)

  const dateFormat =
    type === CommonTableColumnsEnum.openTime ||
    type === CommonTableColumnsEnum.closeTime ||
    type === CommonTableColumnsEnum.date ||
    type === CommonTableColumnsEnum.createdAt

  const isCommissions = [
    CommonTableColumnsEnum.commissionsTotal,
    CommonTableColumnsEnum.commissions,
    CommonTableColumnsEnum.fee,
    CommonTableColumnsEnum.swap,
  ].includes(type as CommonTableColumnsEnum)

  if (type === CommonTableColumnsEnum.status) {
    cellContent = <TradeStatusCell status={value as TradeStatus} />
  }

  if (type === CommonTableColumnsEnum.netProfit) {
    cellContent = (
      <ProfitCell value={!isNaN(value) ? Number(value).toFixed(2) : '-'} currency={currency} />
    )
  }

  if (type === CommonTableColumnsEnum.roi) {
    cellContent = (
      <Typography
        fontWeight={700}
        color={Number(value) < 0 ? palette.red[400] : Number(value) > 0 ? palette.green[400] : ''}
      >
        {isNull(value) || isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`}
      </Typography>
    )
  }

  if (type === CommonTableColumnsEnum.netProfit || type === CommonTableColumnsEnum.grossProfit) {
    cellContent = isNull(value) ? (
      '—'
    ) : (
      <ProfitCell
        value={!isNaN(Number(value)) ? Number(value).toFixed(2) : '—'}
        currency={currentAccount?.currency as string}
      />
    )
  }

  if (type === CommonTableColumnsEnum.openPrice || type === CommonTableColumnsEnum.closePrice) {
    cellContent = isNull(value) || isNaN(Number(value)) ? '—' : currencyFormatter(currency, value)
  }

  if (type === CommonTableColumnsEnum.symbol) {
    cellContent = value ? String(value).toUpperCase() : '—'
  }

  if (type === CommonTableColumnsEnum.type && value) {
    const tradeType = String(value).toLowerCase()
    cellContent = t(`tradingLogPage.tradeType.${tradeType}`, String(value))
  }

  if (type === CommonTableColumnsEnum.cashFlowType && value) {
    const cashFlowType = String(value).toLowerCase()
    cellContent = t(`cashFlowPage.type.${cashFlowType}`, String(value))
  }

  if (type === CommonTableColumnsEnum.platform && value === 'Generic') {
    cellContent = t('tradingAccountsPage.accountForm.platforms.generic', 'Other platform')
  }

  if (type === CommonTableColumnsEnum.method) {
    const importMethod = String(value).toLowerCase()

    if (importMethod === 'manual') {
      cellContent = t('tradingAccountsPage.importHistory.method.manual', 'Manual')
    } else if (importMethod === 'auto' || importMethod === 'auto sync') {
      cellContent = t('tradingAccountsPage.importHistory.method.auto', 'Automatic sync')
    }
  }

  if (dateFormat) {
    const date = parseISO(value)
    cellContent = isValid(date) ? (
      <Box display="flex" flexDirection="column" alignItems="flex-end" width="100%">
        <Typography fontSize="inherit" fontWeight={500} textAlign="right">
          {format(date, 'HH:mm dd MMM yyyy', { locale: dateLocale })}
        </Typography>
        {(type === CommonTableColumnsEnum.date || type === CommonTableColumnsEnum.createdAt) && (
          <Typography variant="caption" color={palette.text.secondary} textAlign="right">
            {formatDistanceToNow(date, { addSuffix: true, locale: dateLocale })}
          </Typography>
        )}
      </Box>
    ) : (
      value || '—'
    )
  }

  if (type === CommonTableColumnsEnum.posValue) {
    cellContent = !isNaN(value) ? currencyFormatter(currency, value) : '—'
  }

  if (type === CommonTableColumnsEnum.amount) {
    cellContent =
      isNull(value) || isNaN(Number(value)) ? (
        '—'
      ) : (
        <ProfitCell
          value={Number(value).toFixed(2)}
          currency={currentAccount?.currency as string}
        />
      )
  }

  if (type === CommonTableColumnsEnum.journalingCompletion) {
    cellContent = <JournalingCell id={id} value={value as string} selectedRow={selectedRow} />
  }

  if (type === CommonTableColumnsEnum.status) {
    cellContent = <TradeStatusCell status={value as TradeStatus} />
  }

  return (
    <TableCell
      onClick={onClick}
      sx={{
        fontSize: { xs: 12, md: 14 },
        fontWeight: 500,
        textAlign: 'right',
        padding: spacing(1.25),
        textTransform: type === CommonTableColumnsEnum.comment ? 'none' : 'capitalize',
        verticalAlign: 'top',
      }}
    >
      <Box
        className="table-cell-content"
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="flex-start"
        width="100%"
        textAlign="right"
        sx={{
          overflowWrap: 'anywhere',
        }}
      >
        {isCommissions && cellContent
          ? currencyFormatter(currentAccount?.currency, Number(cellContent))
          : cellContent || '—'}
      </Box>
    </TableCell>
  )
}
